import { DateTime } from '@ksyos/date-time';
import { Alert, Button, FormGroup, Modal, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type {
    AccountDeactivationEmployment,
    AccountDeactivationPractitioner,
} from '@common/interfaces/api/accountDeactivationApi';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization } from '@common/enums';
import { deactivateEmploymentSchema } from '@common/validation/employment';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import {
    deactivateAccount,
    getActiveEmployments,
    hasActiveRequests,
} from '@frontend/services/api/accountDeactivationApiService';
import { emptyValidationErrors, hasOneAuthorization, updateValidationErrorsForOne, validate } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { getSelectedLanguage } from '../../../translate';
import { HasActiveRequestsModal } from './HasActiveRequestsModal';
import { SelectPractitioner } from './SelectPractitioner';
import { accountDeactivationSchema } from './validation/accountDeactivation';

interface AccountDeactivationProps {
    isOpen: boolean;
    onClose: (accountDeactivated?: boolean) => void;
    organizationId: string;
}

export function AccountDeactivationModal(props: Readonly<AccountDeactivationProps>) {
    const abortSignal = useAbortSignal();
    const [practitioners, setPractitioners] = useState<AccountDeactivationPractitioner[]>([]);
    const [employments, setEmployments] = useState<AccountDeactivationEmployment[]>([]);
    const [practitionerId, setPractitionerId] = useState<string | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isHasActiveRequestsModalOpen, setIsHasActiveRequestsModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showAlertMessage, setShowAlertMessage] = useState<boolean>(false);

    const translate = getSelectedLanguage();
    const hasPractitionerAddEditAuthorization = hasOneAuthorization(ManagerAuthorization.practitionerAddEdit);

    useEffect(() => {
        loadEmployments();
    }, [props.isOpen]);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => props.onClose()}
            size="large"
        >
            <Modal.Header>{translate.practitioner.remove}</Modal.Header>
            <Modal.Body>
                <Text marginBottom={6}>{translate.accountDeactivation.infoText}</Text>
                <FormGroup
                    hasErrors={errors.has('practitionerId')}
                    isRequired={true}
                    label={translate.practitioner.practitioner}
                >
                    <SelectPractitioner
                        errors={errors}
                        onChange={(value) => handleChange(value)}
                        practitioners={practitioners}
                    />
                </FormGroup>
                {showAlertMessage && (
                    <Alert appearance="warning">{translate.accountDeactivation.hasActiveRequestsSupport}</Alert>
                )}
                <FormParagraphFormGroup label={translate.common.endDate}>
                    {DateTime.now().format('DD-MM-YYYY')}
                </FormParagraphFormGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={() => props.onClose()}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>
                <Button
                    isLoading={isLoading}
                    onClick={async () => await handleAccountDeactivation()}
                >
                    {translate.accountDeactivation.deactivate}
                </Button>
            </Modal.Footer>

            {isHasActiveRequestsModalOpen && (
                <HasActiveRequestsModal
                    isOpen={isHasActiveRequestsModalOpen}
                    onClose={handleCloseHasActiveRequestsModal}
                />
            )}
        </Modal>
    );

    async function handleAccountDeactivation() {
        const { errors: validationErrors } = validate(accountDeactivationSchema, {
            practitionerId,
        });

        setErrors(validationErrors);

        if (validationErrors.isEmpty()) {
            const activeEmployment = employments.find(
                (employment: AccountDeactivationEmployment) => employment.practitioner.id === practitionerId
            );
            if (activeEmployment) {
                if (hasPractitionerAddEditAuthorization) {
                    await endEmployment(activeEmployment);
                    props.onClose(true);
                } else {
                    const employmentHasActiveRequests = await hasActiveRequests(activeEmployment.id, abortSignal);
                    if (employmentHasActiveRequests) {
                        setIsHasActiveRequestsModalOpen(true);
                    } else {
                        await endEmployment(activeEmployment);
                        props.onClose(true);
                    }
                }
            }
        }
    }

    async function endEmployment(activeEmployment: AccountDeactivationEmployment) {
        const deactivateEmployment = {
            endDate: DateTime.now().format('YYYY-MM-DD'),
            startDate: activeEmployment.startDate,
        };

        const { errors: deactivateEmploymentValidationErrors, item: deactivateEmploymentValidated } = validate(
            deactivateEmploymentSchema,
            deactivateEmployment
        );

        if (deactivateEmploymentValidationErrors.size > 0) {
            throw new Error('Incorrect employment start or end date.');
        } else if (deactivateEmploymentValidated) {
            await deactivateAccount(activeEmployment.id, deactivateEmploymentValidated, abortSignal);
        }
    }

    async function handleChange(value: string) {
        setIsLoading(true);
        setPractitionerId(value);

        const validationErrors = updateValidationErrorsForOne(
            accountDeactivationSchema,
            { practitionerId: value },
            errors,
            'practitionerId'
        );
        const activeEmployment = employments.find(
            (employment: AccountDeactivationEmployment) => employment.practitioner.id === value
        );
        const employmentHasActiveRequests = activeEmployment
            ? await hasActiveRequests(activeEmployment.id, abortSignal)
            : false;

        setShowAlertMessage(employmentHasActiveRequests && hasPractitionerAddEditAuthorization);
        setErrors(validationErrors);
        setIsLoading(false);
    }

    function handleCloseHasActiveRequestsModal() {
        setIsHasActiveRequestsModalOpen(false);
    }

    async function loadEmployments() {
        const result = await getActiveEmployments(props.organizationId, abortSignal);

        setEmployments(result.items);
        setPractitioners(result.items.map((item: AccountDeactivationEmployment) => item.practitioner));
    }
}
